import React from "react"
import get from "lodash/get"
import ReCAPTCHA from "react-google-recaptcha"
import className from "classnames"
import countryCodes from "country-codes-list"
import FingerprintJS from "@fingerprintjs/fingerprintjs"

import {axios} from "@lib/helpers"
import {toHalfWidth} from "@lib/helpers"

import {load} from "@fingerprintjs/fingerprintjs-pro" // 低レベルAPIを直接インポート

type PropTypes = {
  data: {
    t: RailsI18n.T
    recaptchaSiteKey: string
    initial_tokens: any
    user: any
    paths: any
    errors?: any
  }
}

type CountryData = {
  officialLanguageCode: string
  countryCallingCode: number
  countryNameEn: string
  countryNameLocal: string
  countryCode: string
}

const AppUsersRegistration: React.FC<PropTypes> = ({data}) => {
  const {t, recaptchaSiteKey, initial_tokens, user, paths, errors} = data

  const [formData, setFormData] = React.useState<React.FormData>({
    email: user.email,
    password: user.password,
    password_confirmation: user.password_confirmation,
    phone_locale: user.phone_locale,
    phone_number: user.phone_number,
    name: user.name,
    terms_user_flg: false,
    privacy_policy_flg: false,
  })

  const [submitting, setSubmitting] = React.useState(false)
  const [recaptchaToken, setRecaptchaToken] = React.useState<string | null>(
    null
  )

  const [__errors, __errorsSet] = React.useState(errors)
  const [__isEyeVisiblePassword, __isEyeVisiblePasswordSet] =
    React.useState(false)
  const [
    __isEyeVisiblePasswordConfirmation,
    __isEyeVisiblePasswordConfirmationSet,
  ] = React.useState(false)

  const onReCaptchaChange = (token: string | null) => {
    setRecaptchaToken(token ?? null)
  }

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setFormData((prev) => ({...prev, [name]: value}))
  }

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setFormData((prev) => ({...prev, [name]: toHalfWidth(value)}))
  }

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {name, checked} = e.target
    setFormData((prev) => ({...prev, [name]: checked}))
  }

  const handlePhoneLocaleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFormData((prev) => ({...prev, phone_locale: value}))
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!recaptchaToken) {
      alert("reCAPTCHAを完了してください")
      return
    }

    setSubmitting(true)

    const payload = {
      user: {
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        phone_locale: formData.phone_locale,
        phone_number: formData.phone_number,
        name: formData.name,
        vid: visitorId,
        rid: requestId,
      },
      "g-recaptcha-response": recaptchaToken,
      ...initial_tokens,
    }

    axios
      .post(paths.users_path, payload)
      .then((res: any) => {
        window.location.href = res.data.redirect_url
      })
      .catch((error: any) => {
        const errData = error.response.data

        if (errData.errors) {
          __errorsSet(errData.errors)
        }
        alert(errData.message)
      })
    setSubmitting(false)
  }

  const [visitorId, setVisitorId] = React.useState<string | null>(null)
  const [requestId, setRequestId] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null)

  React.useEffect(() => {
    let isMounted = true

    const initFingerprint = async () => {
      try {
        const agent = await load({
          apiKey: "2f4ZNqaUUxHwU1MDO6We",
          region: "ap", // 日本向け
          // monitoring: true, // デバッグ時は有効化
        })

        const result = await agent.get({
          timeout: 10000,
        })

        if (isMounted) {
          setVisitorId(result.visitorId)
          setRequestId(result.requestId) // これがサーバー検証の鍵
        }
      } catch (err: any) {
        setErrorMsg("デバイス情報の取得に失敗しました")
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    initFingerprint()

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <React.Fragment>
      <h1 className="App__h1">{get(t, "current.register")}</h1>
      <div className="AppUsersNew">
        <div className="AppUsersNew__p">
          {get(t, "current.registered_user")}
          <a href={paths.login_path}>{get(t, "current.here")}</a>
        </div>
        <form
          id="user-registration-form"
          className="AppForm"
          onSubmit={handleSubmit}
          noValidate
        >
          <label className="AppForm__row">
            <div className="AppForm__label">
              <div className="AppForm__label__span">
                {get(t, "dictionary.form.email")}
              </div>
              <div className="AppForm__label__required">
                {get(t, "dictionary.form.required")}
              </div>
            </div>
            <div className="AppForm__value">
              <input
                type="email"
                name="email"
                className="AppInput"
                placeholder="example@example.com"
                autoComplete="username"
                value={formData.email}
                onChange={handleTextChange}
              />
              {__errors &&
                Object.prototype.hasOwnProperty.call(__errors, "email") && (
                  <div className="attr_errors">
                    {__errors["email"].map((error, index) => (
                      <div className="attr_errors__error" key={index}>
                        {`${get(t, "dictionary.form.email")}${error}`}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </label>
          <label className="AppForm__row">
            <div className="AppForm__label">
              <div className="AppForm__label__span">
                {get(t, "dictionary.auth.password")}
              </div>
              <div className="AppForm__label__required">
                {get(t, "dictionary.form.required")}
              </div>
            </div>
            <div className="AppForm__value">
              <div className="Eye">
                <input
                  type={__isEyeVisiblePassword ? "text" : "password"}
                  name="password"
                  className="AppInput"
                  placeholder={get(t, "dictionary.auth.password")}
                  autoComplete="password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                />
                <a
                  className={className("Eye__a", {
                    "Eye__a--active": __isEyeVisiblePassword,
                  })}
                  onClick={() => {
                    __isEyeVisiblePasswordSet(!__isEyeVisiblePassword)
                  }}
                />
              </div>
              {__errors &&
                Object.prototype.hasOwnProperty.call(__errors, "password") && (
                  <div className="attr_errors">
                    {__errors["password"].map((error, index) => (
                      <div className="attr_errors__error" key={index}>
                        {`${get(t, "dictionary.auth.password")}${error}`}
                      </div>
                    ))}
                  </div>
                )}
              <div className="AppForm__value__examples">
                <div className="AppForm__value__examples__p">
                  {get(t, "current.at_least_8_characters")}
                </div>
              </div>
            </div>
          </label>
          <label className="AppForm__row">
            <div className="AppForm__label">
              <div className="AppForm__label__span">
                {get(t, "dictionary.auth.password_confirmation")}
              </div>
              <div className="AppForm__label__required">
                {get(t, "dictionary.form.required")}
              </div>
            </div>
            <div className="AppForm__value">
              <div className="Eye">
                <input
                  type={
                    __isEyeVisiblePasswordConfirmation ? "text" : "password"
                  }
                  name="password_confirmation"
                  className="AppInput"
                  placeholder={get(t, "dictionary.auth.password_confirmation")}
                  autoComplete="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handlePasswordChange}
                />
                <a
                  className={className("Eye__a", {
                    "Eye__a--active": __isEyeVisiblePasswordConfirmation,
                  })}
                  onClick={() => {
                    __isEyeVisiblePasswordConfirmationSet(
                      !__isEyeVisiblePasswordConfirmation
                    )
                  }}
                />
              </div>
              {__errors &&
                Object.prototype.hasOwnProperty.call(
                  __errors,
                  "password_confirmation"
                ) && (
                  <div className="attr_errors">
                    {__errors["password_confirmation"].map((error, index) => (
                      <div className="attr_errors__error" key={index}>
                        {`${get(
                          t,
                          "dictionary.auth.password_confirmation"
                        )}${error}`}
                      </div>
                    ))}
                  </div>
                )}
              <div className="AppForm__value__examples">
                <div className="AppForm__value__examples__p">
                  {get(t, "current.at_least_8_characters")}
                </div>
              </div>
            </div>
          </label>
          <label className="AppForm__row">
            <div className="AppForm__label">
              <div className="AppForm__label__span">
                {get(t, "dictionary.form.phone_number")}
              </div>
              <div className="AppForm__label__required">
                {get(t, "dictionary.form.required")}
              </div>
            </div>
            <div className="AppForm__value">
              <div className="AppForm__value__country-calling-code">
                <select
                  className="AppSelect"
                  name="phone_locale"
                  value={formData.phone_locale}
                  onChange={handlePhoneLocaleChange}
                >
                  {countryCodes?.all?.()?.map?.((x: CountryData, i: number) => (
                    <option value={x?.countryCode?.toLowerCase?.()} key={i}>
                      +{x?.countryCallingCode} {x?.countryNameEn}
                      {x?.countryNameLocal ? ` (${x?.countryNameLocal})` : ""}
                    </option>
                  ))}
                </select>
                <input
                  type="phone_number"
                  name="phone_number"
                  className="AppInput"
                  placeholder="09012345678"
                  value={formData.phone_number}
                  onChange={handleTextChange}
                />
                {__errors &&
                  Object.prototype.hasOwnProperty.call(
                    __errors,
                    "phone_number"
                  ) && (
                    <div className="attr_errors">
                      {__errors["phone_number"].map((error, index) => (
                        <div className="attr_errors__error" key={index}>
                          {`${get(t, "dictionary.form.phone_number")}${error}`}
                        </div>
                      ))}
                    </div>
                  )}
                <div className="AppForm__value__examples">
                  <div className="AppForm__value__examples__p">
                    {get(t, "current.without_hyphen")}
                  </div>
                </div>
              </div>
            </div>
          </label>

          <label className="AppForm__row">
            <div className="AppForm__label">
              <div className="AppForm__label__span">
                {get(t, "dictionary.form.name")}
              </div>
              <div className="AppForm__label__required">
                {get(t, "dictionary.form.required")}
              </div>
            </div>
            <div className="AppForm__value">
              <input
                type="text"
                name="name"
                className="AppInput"
                placeholder={get(t, "dictionary.form.placeholder.name")}
                value={formData.name}
                onChange={handleTextChange}
              />
              {__errors &&
                Object.prototype.hasOwnProperty.call(__errors, "name") && (
                  <div className="attr_errors">
                    {__errors["name"].map((error, index) => (
                      <div className="attr_errors__error" key={index}>
                        {`${get(t, "dictionary.form.name")}${error}`}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </label>
          <label className="AppForm__checkbox">
            <input
              type="checkbox"
              className="AppForm__checkbox__input"
              id="terms_user_flg"
              name="terms_user_flg"
              checked={formData.terms_user_flg}
              onChange={handleCheckboxChange}
            />
            <div className="AppForm__checkbox__wrapper">
              <div className="AppForm__checkbox__label">
                <a
                  href={paths.terms_user_path}
                  target="_blank"
                  className="AppForm__checkbox__label__a"
                  rel="noreferrer"
                >
                  {get(t, "dictionary.contract.terms_user")}
                </a>
                {get(t, "current.agree")}
              </div>
            </div>
          </label>
          <label className="AppForm__checkbox">
            <input
              type="checkbox"
              className="AppForm__checkbox__input"
              id="privacy_policy_flg"
              name="privacy_policy_flg"
              checked={formData.privacy_policy_flg}
              onChange={handleCheckboxChange}
            />
            <div className="AppForm__checkbox__wrapper">
              <div className="AppForm__checkbox__label">
                <a
                  href={paths.privacy_policy_path}
                  target="_blank"
                  className="AppForm__checkbox__label__a"
                  rel="noreferrer"
                >
                  {get(t, "dictionary.contract.privacy_policy")}
                </a>
                {get(t, "current.agree")}
              </div>
            </div>
          </label>

          <div style={{margin: "1.5rem 0"}}>
            <ReCAPTCHA
              sitekey={recaptchaSiteKey}
              onChange={onReCaptchaChange}
            />
          </div>
          <div className="AppForm__row">
            <button
              type="submit"
              className="Button Button--large"
              id="register_user"
              disabled={
                submitting ||
                !formData.terms_user_flg ||
                !formData.privacy_policy_flg ||
                recaptchaToken == null
              }
            >
              {submitting ? "登録中..." : "新規登録"}
            </button>
          </div>
        </form>
      </div>
    </React.Fragment>
  )
}

export default AppUsersRegistration
